import { Button, Grid, LinearProgress, Typography } from "@material-ui/core";
import { useMutation } from "@tanstack/react-query";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { MentorTip } from "../../../components";
import { FooterActions } from "../../../components/wizard/footer-actions.component";
import { Status } from "../../../models/gameplan";
import { Goal } from "../../../shared/components/goal";
import { useApiParticipantFocus } from "../../../shared/hooks";
import { getActiveCourse } from "../../../shared/slices/activeCourse";
import { setCourseCommitmentStatusById } from "../../../shared/slices/data";
import { RootState } from "../../../store";
import { patchFocus } from "../../learningPath.service";
import { commitGoalStyles } from "./commit-goal.styles";

const answers = ["Once", "Twice", "Three", "Three+"];

type Selector = {
  participantProfileId: RootState["profile"]["participantProfileId"];
  activeLearningPath: RootState["activeLearningPath"];
};

export const CommitGoal = () => {
  const classes = commitGoalStyles();
  const dispatch = useDispatch();
  const activeCourseId = useSelector(getActiveCourse);
  const [answerSelected, setAnswerSelected] = useState("");
  const { useQuery } = useApiParticipantFocus();

  const { participantProfileId, activeLearningPath } = useSelector<
    RootState,
    Selector
  >((state) => ({
    participantProfileId: state.profile.participantProfileId,
    activeLearningPath: state.activeLearningPath,
  }));

  const { data: participantFocus, isLoading } = useQuery({
    profileId: participantProfileId,
    focusId: activeLearningPath,
  });

  const { mutateAsync, isLoading: isUpdating } = useMutation(patchFocus, {
    onSuccess: () => {
      dispatch(setCourseCommitmentStatusById(activeCourseId, Status.COMPLETED));
    },
  });

  useEffect(() => {
    setAnswerSelected(participantFocus?.commitedTime);
  }, [participantFocus?.commitedTime]);

  const nextButtonAction = async () => {
    if (participantFocus?.commitedTime != answerSelected)
      await mutateAsync({
        commitedTime: answerSelected,
        participantProfileId,
      });
  };

  const selectAnswer = (answer: string) => {
    setAnswerSelected(answer);
  };

  const mentorTip = () => (
    <div>
      <ul>
        <li>
          <Typography variant="body1">
            Habits have the power to bridge the gap between your focus and your
            intentions
          </Typography>
        </li>
        <li>
          <Typography variant="body1">
            When you know where your money is going, you’ll discover ways to
            spend less as well as find motivation to stick with your habits
          </Typography>
        </li>
      </ul>
    </div>
  );

  return (
    <>
      {(isLoading || isUpdating) && (
        <div data-testid="loading">
          <LinearProgress color="secondary" />
        </div>
      )}
      {!isLoading && (
        <>
          <Grid item xs={12}>
            <Typography variant="h6" className="title">
              Commit to your goal
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Goal columnGap={10} variant="avatar" id="gameplan-wt-3" />
          </Grid>
          <div id="gameplan-wt-4">
            <Grid item xs={12}>
              <Typography variant="h6" className={classes.question}>
                How much time would you like to invest to reach your goal?
              </Typography>
            </Grid>
            <div className={classes.answerContainer}>
              {answers.map((answer) => (
                <Button
                  className={clsx(classes.answer, {
                    [classes.answerSelected]: answerSelected == answer,
                  })}
                  key={answer}
                  onClick={() => selectAnswer(answer)}
                >
                  {answer} / week
                </Button>
              ))}
            </div>
          </div>
          <MentorTip
            className={classes.tipDetails}
            title={
              <Typography variant="body1" className={classes.tipTitle}>
                Mentor Tip
              </Typography>
            }
            details={mentorTip()}
          />
        </>
      )}
      <FooterActions
        backShow={false}
        nextDisabled={!answerSelected || isUpdating}
        nextAction={nextButtonAction}
      />
    </>
  );
};
